var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { get, isEmpty, isFunction } from "lodash";
import { PERMISSION_ACTION, STORE_LEVEL } from "../constants";
import { isPermissionSystemFlagEnabled } from "./featureFlagHelper";
import { isSelectedMerchantVE } from "./merchantSelectorHelper";
export var permissionKeysSet = new Set();
export var resourcePermissionsMap = new Map();
export var parentResourceMap = new Map();
export var resourceKeysSet = new Set();
var addPermissionToMap = function (map, key) {
    var _a;
    var values = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        values[_i - 2] = arguments[_i];
    }
    if (!map.has(key)) {
        map.set(key, []);
    }
    (_a = map.get(key)) === null || _a === void 0 ? void 0 : _a.push.apply(_a, __spreadArray([], __read(values), false));
};
/**
 * Processes a resource by adding its permissions to the resource permissions map and its key to the resource keys set.
 * If the resource has a parent, it also sets the parent-child relationship in the parent resource map.
 * This function is recursive and will process any nested child resources.
 *
 * @param {Resource} resource - The resource to process.
 * @param {string|null} parentKey - The key of the parent resource, or null if the resource is a top-level resource.
 * @param {string[]} permissionKeys - An array of permission keys to be associated with the resource.
 */
var processResource = function (resource, parentKey, permissionKeys) {
    var _a;
    var resourceKey = "".concat(resource.resourceType, ":").concat(resource.externalID);
    if (parentKey) {
        parentResourceMap.set(resourceKey, parentKey);
    }
    addPermissionToMap.apply(void 0, __spreadArray([resourcePermissionsMap, resourceKey], __read(permissionKeys), false));
    resourceKeysSet.add(resourceKey);
    (_a = resource.children) === null || _a === void 0 ? void 0 : _a.forEach(function (child) {
        processResource(child, resourceKey, permissionKeys);
    });
};
/**
 * Initializes permissions based on a given PermissionResponse object.
 * This is processed into merchant level and store level maps for easier retrieval.
 * If the permissionsResponse is invalid, it clears the permissions data.
 * @param permissionsResponse - The permissions response to initialize the permissions with.
 */
export var initializePermissions = function (permissionsResponse) {
    if (!permissionsResponse || !permissionsResponse.statements || !Array.isArray(permissionsResponse.statements)) {
        clearPermissionsData();
        return;
    }
    permissionKeysSet.clear();
    resourcePermissionsMap.clear();
    parentResourceMap.clear();
    resourceKeysSet.clear();
    permissionsResponse.statements.forEach(function (statement) {
        var resources = statement.resources, role = statement.role;
        var permissionKeys = role.permissions.map(function (perm) { return "".concat(perm.action, ":").concat(perm.module); });
        permissionKeysSet = new Set(__spreadArray(__spreadArray([], __read(permissionKeysSet), false), __read(permissionKeys), false));
        resources.forEach(function (res) {
            processResource(res, null, permissionKeys);
        });
    });
    var userPermissionData = {
        permissionKeysSet: __spreadArray([], __read(permissionKeysSet), false),
        resourcePermissionsMap: __spreadArray([], __read(resourcePermissionsMap.entries()), false),
        parentResourceMap: __spreadArray([], __read(parentResourceMap.entries()), false),
        resourceKeysSet: __spreadArray([], __read(resourceKeysSet), false),
        permissionsResponse: permissionsResponse,
    };
    localStorage.setItem("userPermissionData", JSON.stringify(userPermissionData));
};
export var getUserPermissionData = function () {
    try {
        var userPermissionDataString = localStorage.getItem("userPermissionData");
        if (userPermissionDataString) {
            var userPermissionData = JSON.parse(userPermissionDataString);
            userPermissionData.permissionKeysSet = new Set(userPermissionData.permissionKeysSet);
            userPermissionData.resourcePermissionsMap = new Map(userPermissionData.resourcePermissionsMap);
            userPermissionData.parentResourceMap = new Map(userPermissionData.parentResourceMap);
            userPermissionData.resourceKeysSet = new Set(userPermissionData.resourceKeysSet);
            return userPermissionData;
        }
        return null;
    }
    catch (error) {
        console.error("Error retrieving combined data from localStorage:", error);
        return null;
    }
};
/**
 * Clears the permissions data from local storage.
 */
export var clearPermissionsData = function () {
    localStorage.removeItem("userPermissionData");
    localStorage.removeItem("selectedResource");
    localStorage.removeItem("merchantSelector");
};
export var getPermissionsResponse = function () {
    var userPermissionData = getUserPermissionData();
    return userPermissionData ? userPermissionData.permissionsResponse : {};
};
export var getParentResourceMap = function () {
    var userPermissionData = getUserPermissionData();
    return userPermissionData ? userPermissionData.parentResourceMap : new Map();
};
export var getResourcePermissionsMap = function () {
    var userPermissionData = getUserPermissionData();
    return userPermissionData ? userPermissionData.resourcePermissionsMap : new Map();
};
export var getPermissionKeysSet = function () {
    var userPermissionData = getUserPermissionData();
    return userPermissionData ? userPermissionData.permissionKeysSet : new Set();
};
export var setSelectedResource = function (resourceID, resourceType) {
    if (!resourceID || !resourceType) {
        return;
    }
    try {
        return localStorage.setItem("selectedResource", JSON.stringify("".concat(resourceType, ":").concat(resourceID)));
    }
    catch (err) {
        return '';
    }
};
export var appendSelectedVEStoresResource = function (resources) {
    try {
        var selectedResource = JSON.parse(localStorage.getItem("selectedResource") || "");
        var _a = __read(selectedResource.split(','), 1), merchantResource = _a[0];
        var selectedStores = resources.map(function (resourceID) { return "".concat(STORE_LEVEL, ":").concat(resourceID); });
        localStorage.setItem("selectedResource", JSON.stringify(__spreadArray([merchantResource], __read(selectedStores), false).join(',')));
    }
    catch (err) {
        console.error("Error appending selected resources:", err);
    }
};
export var getSelectedResource = function () {
    try {
        var selectedResource = JSON.parse(localStorage.getItem("selectedResource") || "");
        return selectedResource;
    }
    catch (err) {
        return '';
    }
};
export var getResourceKeys = function () {
    try {
        if (getSelectedResource() && isSelectedMerchantVE()) {
            return getSelectedResource();
        }
        var userPermissionData = getUserPermissionData();
        if (!userPermissionData || !userPermissionData.resourceKeysSet) {
            return "";
        }
        return Array.from(userPermissionData.resourceKeysSet).join(",");
    }
    catch (err) {
        return '';
    }
};
/**
 * Checks if the user permission system is being used through feature flag and user_permission: 1 from login api.
 * @returns {boolean} True if the user permission system is active and not observer, false otherwise.
 */
export var hasUserPermissionSystem = function () {
    var usingUserPermissionSystem = false;
    try {
        var userProfileInfo = JSON.parse(localStorage.getItem('userprofileInfo') || '{}');
        var isObserver = get(userProfileInfo, ["user_profile", "is_observer"], false);
        usingUserPermissionSystem = !!get(userProfileInfo, ["user_profile", "user_permission"], 0) && !isObserver;
    }
    catch (err) {
        usingUserPermissionSystem = false;
    }
    return isPermissionSystemFlagEnabled() && usingUserPermissionSystem;
};
/**
 * Determines whether the user permission system is active via feature flag and has user_permission: 1 from login api as well as a non-empty permissions response. If this is false, we can fallback to the legacy permission system.
 * @returns {boolean} True if the user permission system is active and the permissions response is not empty.
 */
export var isUserPermissionSystemActive = function () {
    return hasUserPermissionSystem() && !isEmpty(getPermissionsResponse());
};
/**
 * Checks if a user has a specific permission for a given action and module.
 * @param {HasPermissionParams} hasPermissionParams - The parameters for the permission check.
 * @returns {boolean} True if the user has the specified permission, false otherwise. Refer to example below for all the parameters.
 *  @example
 * // Example of checking permission to edit in the 'dashboard' module including all the parameters
 * const canEditDashboard = hasPermission({
 *   action: PERMISSION_ACTION.EDIT,
 *   module: PERMISSION_MODULE.DASHBOARD,
 *   legacyPermissionCheck: () => isCashier() && !GP_MERCHANT(),
 *   customComparisonFunc: (permissionToCheck, existingPermission) =>
 *      existingPermission.startsWith(permissionToCheck),
 * });
 *
 *
 * @example
 * // Example of checking permission to edit in the 'dashboard' module
 * const canEditDashboard = hasPermission({
 *   action: PERMISSION_ACTION.EDIT,
 *   module: PERMISSION_MODULE.DASHBOARD,
 * });
 *
 * @example
 * // Checking permission with a custom comparison function
 * const hasCustomPermission = hasPermission({
 *   action: PERMISSION_ACTION.GET,
 *   module: PERMISSION_MODULE.INVENTORY,
 *   customComparisonFunc: (permissionToCheck, existingPermission) =>
        existingPermission.startsWith(permissionToCheck), //if you only want to check the prefix
 * });
 *
 * @example
 * // Using legacy permission check
 * const canViewInventoryLegacy = hasPermission({
 *   action: PERMISSION_ACTION.GET,
 *   module: PERMISSION_MODULE.INVENTORY,
 *   legacyPermissionCheck: () => isCashier() && !GP_MERCHANT()
 * });
 */
export var hasPermission = function (_a) {
    var _b;
    var action = _a.action, module = _a.module, _c = _a.legacyPermissionCheck, legacyPermissionCheck = _c === void 0 ? null : _c, _d = _a.customComparisonFunc, customComparisonFunc = _d === void 0 ? null : _d;
    if (!isUserPermissionSystemActive() || !module) { //If module is empty, treat the module as not onboarded
        return (_b = legacyPermissionCheck === null || legacyPermissionCheck === void 0 ? void 0 : legacyPermissionCheck()) !== null && _b !== void 0 ? _b : true;
    }
    var permissionKey = "".concat(action, ":").concat(module);
    var matchPermission = isFunction(customComparisonFunc)
        ? function (existingPermissions, key) {
            return existingPermissions.some(function (existingPermission) { return customComparisonFunc === null || customComparisonFunc === void 0 ? void 0 : customComparisonFunc(key, existingPermission); });
        }
        : function (existingPermissions, key) { return existingPermissions.includes(key); };
    var permissionFound = false;
    var permissionKeysSet = getPermissionKeysSet();
    if (matchPermission(Array.from(permissionKeysSet), permissionKey)) {
        permissionFound = true;
    }
    return permissionFound;
};
/**
 * Checks if a user has view permission for a specific module. This is just a wrapper around the hasPermission function for View action for convenience.
 * @param {HasPermissionToViewParams} params - The parameters for the view permission check.
 * @returns {boolean} True if the user has view permission for the specified module, false otherwise.
 */
export var hasPermissionToView = function (_a) {
    var module = _a.module, _b = _a.legacyPermissionCheck, legacyPermissionCheck = _b === void 0 ? null : _b, _c = _a.customComparisonFunc, customComparisonFunc = _c === void 0 ? null : _c;
    return hasPermission({
        action: PERMISSION_ACTION.GET,
        module: module,
        legacyPermissionCheck: legacyPermissionCheck,
        customComparisonFunc: customComparisonFunc
    });
};
//# sourceMappingURL=permissionsUtils.js.map